import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Users, Gift, Loader2, Wallet, IndianRupee, X, Share2,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import api from "../lib/api";
import { getUser, clearAuth } from "../lib/auth";

export default function ReferEarn() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/referral/me");
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAuth();
        nav("/login", { replace: true });
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const referralUrl = data?.referral_code
    ? `${window.location.origin}/signup?ref=${data.referral_code}`
    : "";

  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); } catch { toast.error("Couldn't copy"); }
  };

  const share = async () => {
    const msg = `I just pre-booked GGES Ventures — join with my code ${data.referral_code} and get 10% off. ${referralUrl}`;
    if (navigator.share) { try { await navigator.share({ title: "GGES Ventures", text: msg, url: referralUrl }); return; } catch {} }
    copy(msg, "Share text copied");
  };

  if (loading || !data) {
    return (
      <AppShell>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <WithdrawModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        available={data.available_inr}
        minimum={data.min_withdraw_inr}
        onDone={() => { setWithdrawOpen(false); load(); }}
      />

      <div className="min-h-screen bg-white">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-5">
          <button onClick={() => nav(-1)} data-testid="refer-back-btn" className="w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-50">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Logo size="sm" />
          <div className="w-9" />
        </div>

        {/* Hero banner */}
        <div className="mx-5 mt-5 relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#ff7a1a] via-[#ff5a00] to-[#e64500] text-white p-6">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white text-[10px] font-bold tracking-widest">
              <Gift className="w-3 h-3" /> REFER & EARN 25%
            </div>
            <h1 className="mt-3 font-display font-black text-[28px] tracking-tighter leading-tight">
              Earn ₹{data.earning_per_signup_inr.toLocaleString("en-IN")}
              <br />per friend who joins.
            </h1>
            <p className="mt-2 text-[13px] text-white/85 leading-relaxed">
              Friends who use your code get an extra {data.extra_discount_percent}% off. You earn 25% of every successful pre-booking.
            </p>
          </div>
        </div>

        {/* Balance cards */}
        <div className="mx-5 mt-4 grid grid-cols-3 gap-2">
          <BalanceCard label="Total earned" value={`₹${data.referral_earnings_inr.toLocaleString("en-IN")}`} testId="ref-earned" />
          <BalanceCard label="Withdrawn" value={`₹${data.withdrawn_inr.toLocaleString("en-IN")}`} testId="ref-withdrawn" />
          <BalanceCard label="Available" value={`₹${data.available_inr.toLocaleString("en-IN")}`} highlight testId="ref-available" />
        </div>

        {/* Withdraw CTA */}
        <div className="mx-5 mt-4 flex items-center gap-2">
          <button
            onClick={() => setWithdrawOpen(true)}
            disabled={data.available_inr < data.min_withdraw_inr}
            data-testid="withdraw-open-btn"
            className={`h-11 flex-1 rounded-full font-semibold flex items-center justify-center gap-2 text-[13.5px] ${
              data.available_inr >= data.min_withdraw_inr ? "btn-brand" : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
            }`}
          >
            <Wallet className="w-4 h-4" /> Withdraw
          </button>
          <button onClick={share} data-testid="ref-share-btn" className="h-11 px-4 rounded-full border border-neutral-200 flex items-center gap-1.5 text-[13px] font-semibold">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
        <div className="mx-5 mt-1.5 text-[11px] text-neutral-500 text-center">
          Minimum withdrawal ₹{data.min_withdraw_inr}
        </div>

        {/* Referral link card */}
        <div className="mx-5 mt-6 p-4 rounded-2xl border border-neutral-200 bg-white">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Your referral code</div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="font-mono font-bold text-[22px] tracking-widest text-black" data-testid="ref-code">
              {data.referral_code}
            </div>
            <button onClick={() => copy(data.referral_code, "Code copied")} data-testid="ref-copy-code" className="h-9 px-3 rounded-full border border-neutral-200 flex items-center gap-1.5 text-[12px] font-semibold">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>
          <div className="mt-3 border-t border-neutral-100 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Referral link</div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-[12px] text-neutral-700 truncate" data-testid="ref-url">
                {referralUrl}
              </div>
              <button onClick={() => copy(referralUrl, "Link copied")} data-testid="ref-copy-url" className="h-9 px-3 rounded-full bg-black text-white text-[12px] font-semibold">
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* Referred users list */}
        <div className="mx-5 mt-6">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">
              Your referrals ({data.referral_count || 0})
            </div>
          </div>
          {data.referrals.length === 0 ? (
            <div className="mt-3 p-6 rounded-2xl border border-dashed border-neutral-200 text-center">
              <Users className="w-6 h-6 text-neutral-300 mx-auto" />
              <div className="mt-2 text-[13px] text-neutral-500">No referrals yet. Share your code to start earning.</div>
            </div>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="ref-list">
              {data.referrals.map((r, i) => (
                <li key={i} className="p-3 rounded-2xl border border-neutral-200 bg-white flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[13.5px]">{r.name}</div>
                    <div className="text-[11.5px] text-neutral-500">{r.phone}{r.city ? ` · ${r.city}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-[15px] text-emerald-600 tabular-nums">+₹{r.amount_inr.toLocaleString("en-IN")}</div>
                    <div className="text-[10px] text-neutral-400">{(r.created_at || "").slice(0, 10)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Withdrawal history */}
        {data.withdrawals.length > 0 && (
          <div className="mx-5 mt-6 pb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Withdrawal history</div>
            <ul className="mt-3 space-y-2" data-testid="withdraw-list">
              {data.withdrawals.map((w) => (
                <li key={w.id} className="p-3 rounded-2xl border border-neutral-200 bg-white flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[13.5px]">₹{w.amount_inr.toLocaleString("en-IN")}</div>
                    <div className="text-[11px] text-neutral-500">{w.method.toUpperCase()} · {(w.created_at || "").slice(0, 10)}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    w.status === "paid" ? "bg-emerald-50 text-emerald-600" :
                    w.status === "approved" ? "bg-sky-50 text-sky-600" :
                    w.status === "rejected" ? "bg-red-50 text-red-600" :
                    "bg-amber-50 text-amber-600"
                  }`}>{w.status.toUpperCase()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function BalanceCard({ label, value, highlight, testId }) {
  return (
    <div className={`p-3 rounded-2xl border ${highlight ? "border-brand/40 bg-[#FFF6EE]" : "border-neutral-200 bg-white"}`} data-testid={testId}>
      <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className="mt-1 font-display font-bold text-[16px] tabular-nums leading-tight">{value}</div>
    </div>
  );
}

function WithdrawModal({ open, onClose, available, minimum, onDone }) {
  const [method, setMethod] = useState("upi");
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState("");
  const [acc, setAcc] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setAmount(""); setUpi(""); setAcc(""); setIfsc(""); setName(""); setMethod("upi"); }
  }, [open]);

  if (!open) return null;

  const amt = parseInt(amount || "0", 10);
  const canSubmit = amt >= minimum && amt <= available &&
    (method === "upi" ? upi.includes("@") : (acc && ifsc && name));

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/referral/withdraw", {
        amount_inr: amt, method,
        upi_id: method === "upi" ? upi.trim() : null,
        account_number: method === "bank" ? acc.trim() : null,
        ifsc: method === "bank" ? ifsc.trim().toUpperCase() : null,
        account_name: method === "bank" ? name.trim() : null,
      });
      toast.success("Withdrawal requested");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Withdrawal failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="withdraw-modal">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="pt-2 pb-1 flex justify-center"><span className="w-10 h-1 bg-neutral-200 rounded-full" /></div>
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-neutral-100">
          <div>
            <div className="font-display font-bold text-[18px]">Withdraw funds</div>
            <div className="text-[11px] text-neutral-500">Available ₹{available.toLocaleString("en-IN")} · Minimum ₹{minimum}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Amount (INR)</label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                data-testid="withdraw-amount-input"
                inputMode="numeric"
                type="number"
                min={minimum}
                max={available}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
                className="input-native pl-10"
                placeholder={`Minimum ${minimum}`}
              />
            </div>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-neutral-700 block mb-2">Payout method</label>
            <div className="flex gap-2">
              {["upi", "bank"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMethod(m)}
                  data-testid={`withdraw-method-${m}`}
                  className={`flex-1 h-10 rounded-full text-[13px] font-semibold ${method === m ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}
                >
                  {m === "upi" ? "UPI" : "Bank transfer"}
                </button>
              ))}
            </div>
          </div>

          {method === "upi" ? (
            <div>
              <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">UPI ID</label>
              <input data-testid="withdraw-upi-input" className="input-native" placeholder="yourname@bank" value={upi} onChange={(e) => setUpi(e.target.value)} />
            </div>
          ) : (
            <>
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Account holder name</label>
                <input data-testid="withdraw-name-input" className="input-native" placeholder="As per bank" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">Account number</label>
                <input data-testid="withdraw-acc-input" inputMode="numeric" className="input-native" placeholder="10-16 digit account" value={acc} onChange={(e) => setAcc(e.target.value.replace(/\D/g, ""))} />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-neutral-700 block mb-1.5">IFSC</label>
                <input data-testid="withdraw-ifsc-input" className="input-native uppercase" placeholder="e.g. HDFC0000123" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} maxLength={11} />
              </div>
            </>
          )}

          <button
            onClick={submit}
            disabled={loading || !canSubmit}
            data-testid="withdraw-submit-btn"
            className="btn-brand w-full h-11 flex items-center justify-center text-[14px] mt-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Request ₹${amt || 0}`}
          </button>
          <div className="text-[11px] text-neutral-500 text-center">
            Requests are usually processed within 24-72 hours.
          </div>
        </div>
      </div>
    </div>
  );
}
